import { useEffect, useRef, useState } from "react";

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (name: string) => Promise<void> | void;
  isSubmitting?: boolean;
  errorMessage?: string;
}

// Selector for tabbable nodes inside the modal — kept minimal; matches all the elements we render.
const FOCUSABLE_SELECTOR =
  "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

export function SaveViewDialog({ open, onClose, onSave, isSubmitting, errorMessage }: Props) {
  const [name, setName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setName("");
    // Save the previously focused element so we can restore focus when the dialog closes.
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const handle = setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      clearTimeout(handle);
      // Restore focus on close.
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  // Focus trap: when Tab/Shift+Tab reaches a boundary, cycle within the dialog.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !modalRef.current) return;
      const focusables = Array.from(
        modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  const trimmed = name.trim();
  const canSave = trimmed.length > 0 && !isSubmitting;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        ref={modalRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="save-view-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
        }}
      >
        <h3 id="save-view-title" style={{ margin: "0 0 12px 0" }}>
          Save current view
        </h3>
        <label style={{ display: "block", marginBottom: 12 }}>
          <span style={{ fontSize: 12, color: "var(--fg-2)" }}>Name</span>
          <input
            ref={inputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. ACME criticals last 90d"
            maxLength={200}
            style={{
              width: "100%",
              padding: "6px 10px",
              border: "1px solid var(--border-subtle)",
              borderRadius: 4,
              fontSize: 14,
              marginTop: 4,
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canSave) void onSave(trimmed);
            }}
          />
        </label>
        {errorMessage && (
          <div role="alert" style={{ color: "var(--gw-error-600)", fontSize: 12, marginBottom: 12 }}>
            {errorMessage}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => canSave && void onSave(trimmed)}
            disabled={!canSave}
          >
            {isSubmitting ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
